import { MODULE_SPEC_SECTION_SELECTOR } from '../data/moduleSpecContracts';
import type { PrototypeEntry } from '../data/contracts';

export interface TourCandidate extends PrototypeEntry {
  group: string | null;
  separatorBefore: boolean;
  active: boolean;
  icon: string | null;
  /** F2 : indice du conteneur de navigation (`nav`, `[role=navigation]`) qui porte l'élément, -1 hors d'un tel conteneur. */
  container?: number;
  /** F2 : côté de l'écran occupé par le menu qui porte l'élément (rectangle du panneau mesuré par l'agent). */
  region?: 'left' | 'top' | 'bottom' | 'unknown';
}
/** F2 : un élément du menu vu par l'agent ; `selector` est nul quand aucun sélecteur simple n'est unique dans la vue affichée. */
export type MenuFact = Omit<TourCandidate, 'selector'> & { selector: string | null };
export const TOUR_MAX_CANDIDATES = 60;
export const TOUR_DENY = 'supprim|effac|delete|remove|reset|remise|repartir de z|partir de z|vider|export|import|imprim|print|deconnex|logout|quitter|envoyer|send|payer|pay|confirmer la suppression';
export const safeTourLabel = (label: string): boolean =>
  !new RegExp(TOUR_DENY).test(label.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase());
export const validTourSelector = (value: unknown): value is string =>
  typeof value === 'string' && value.length <= 120 && MODULE_SPEC_SECTION_SELECTOR.test(value);
export const planTour = (candidates: TourCandidate[], existing: ReadonlySet<string>, max = 40): TourCandidate[] => {
  const seen = new Set(existing);
  return candidates.slice(0, TOUR_MAX_CANDIDATES).sort((a, b) => Number(b.active) - Number(a.active)).filter((candidate) => {
    if (!validTourSelector(candidate.selector) || !safeTourLabel(candidate.label) || seen.has(candidate.selector)) return false;
    seen.add(candidate.selector);
    return true;
  }).slice(0, Math.max(0, Math.min(40, max)));
};
export const tourFingerprint = (doc: Document): string =>
  [...Array.from(doc.querySelectorAll('main h1,main h2,dialog[open] h2')).map((h) => h.textContent ?? ''),
    (doc.querySelector('dialog[open]') ?? doc.querySelector('main') ?? doc.body).textContent?.replace(/\s+/g, ' ').slice(0, 2000) ?? ''].join('|');
/** E2E-QUIZ-CMDB (GAP-22, passe réelle n° 2 de Finances) : une entrée d'en-tête prend tout le texte du bouton (« FO Foyer Martin
 * Paramètres du foyer » : initiales, nom, écran). Quand le titre de l'écran affiché (premier h1, sinon h2, hors en-tête, menus et
 * panneaux latéraux) figure dans ce libellé sans l'égaler, la capture prend le titre de l'écran. Un libellé de menu propre reste. */
export const screenTourLabel = (label: string, doc: Document): string => {
  const clean = (value: string | null | undefined) => (value ?? '').replace(/\s+/g, ' ').trim();
  const outside = (element: Element) => !element.closest('header,nav,aside,[role=banner],[role=navigation],[role=complementary],[hidden],[aria-hidden=true]');
  const heading = [...Array.from(doc.querySelectorAll('h1')), ...Array.from(doc.querySelectorAll('h2'))].find(outside);
  const title = clean(heading?.textContent);
  const fold = (value: string) => value.normalize('NFKD').replace(/\p{M}/gu, '').toLocaleLowerCase();
  return title && title.length <= 120 && fold(label) !== fold(title) && fold(label).includes(fold(title)) ? title : label;
};
